//used in client
import {
  isPayoutProvider,
  PayoutProvider,
  PayoutProviderCustomerType,
} from './PayoutProvider.types';
import { CurrencyUnit, isCurrencyUnit } from '../Currency/Currency.types';
import {
  booleanValidator,
  objectValidator,
  stringValidator,
} from '../../validate';
import { WiseConfirmationOutcome } from './Wise.types';

export interface PayoutAccount {
  id: string;
  createdAt: string;
  updatedAt: string;
  providerID: string;
  providerName: PayoutProvider;
  providerCustomerID: string;
  providerCustomerAccountID?: PayoutProviderCustomerType;
  displayName: string;
  isDefault: boolean;
  currency: CurrencyUnit;
}

export interface PayoutAccountConfirmationRequired {
  confirmationRequired: true;
  accountID: string;
  outcomes: WiseConfirmationOutcome[];
}

export type CreatePayoutAccountResponse =
  | PayoutAccount
  | PayoutAccountConfirmationRequired;

export type VerificationOfPayeeResponse = CreatePayoutAccountResponse;

export const isPayoutAccountConfirmationRequired = (
  value: CreatePayoutAccountResponse,
): value is PayoutAccountConfirmationRequired =>
  'confirmationRequired' in value && value.confirmationRequired;

export function isPayoutAccount(
  value: unknown,
): asserts value is PayoutAccount {
  objectValidator(value, 'payoutAccount');

  const {
    id,
    createdAt,
    updatedAt,
    providerID,
    providerName,
    providerCustomerID,
    displayName,
    isDefault,
    currency,
  } = value;

  const requiredStrings = [
    { name: 'payoutAccount.id', value: id },
    { name: 'payoutAccount.createdAt', value: createdAt },
    { name: 'payoutAccount.updatedAt', value: updatedAt },
    { name: 'payoutAccount.providerID', value: providerID },
    { name: 'payoutAccount.providerCustomerID', value: providerCustomerID },
    { name: 'payoutAccount.displayName', value: displayName },
  ];

  requiredStrings.forEach((str) => {
    stringValidator(str.value, str.name, { nonEmpty: true });
  });

  const requiredBooleans = [
    { name: 'payoutAccount.isDefault', value: isDefault },
  ];

  requiredBooleans.forEach((bool) => {
    booleanValidator(bool.value, bool.name);
  });

  isCurrencyUnit(currency);
  isPayoutProvider(providerName);
}
